import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Download, Tag, Gift, ClipboardList, Search, Trash2 } from 'lucide-react';
import { formatPrice, brandifyText } from '../lib/utils';

interface Order {
  id: number;
  orderNum: string;
  createdAt: number;
  name: string;
  phone: string;
  items: any[];
  totalPrice: number;
  discountAmount: number;
  finalPrice: number;
  giftCode?: string;
  paymentConfirmed: number;
  orderCompleted: number;
  status?: string;
  paymentProof?: string;
  referralCode?: string;
  referrerPhone?: string;
  region?: string;
  address?: string;
  attachedGiftsCount?: number;
}

interface OrderTag {
  key: string;
  label: string;
  color: string;
}

interface Filters {
  searchTerm: string;
  paymentStatus: string;
  tag: string;
  region: string;
  hasGift: string;
  status: string;
}

const DELIVERY_REGIONS = [
  { value: 'HK', label: '香港島' },
  { value: 'KLN', label: '九龍' },
  { value: 'NT', label: '新界' },
  { value: 'OS', label: '海外' },
];

const ALL_TAGS = [
  { key: 'new-customer', label: '新客戶' },
  { key: 'returning-customer', label: '回頭客' },
  { key: 'vip', label: 'VIP' },
  { key: 'big-order', label: '大單' },
  { key: 'standard-order', label: '標準單' },
  { key: 'referred', label: '推薦單' },
];

// 狀態膠囊用真 status 欄（order_records.state machine）；舊數據缺 status 時按 boolean 後備
const ORDER_STATUS_META: Record<string, { label: string; color: string }> = {
  pending_whatsapp: { label: '待客戶確認', color: 'bg-amber-100 text-amber-700' },
  pending_payment: { label: '待付款', color: 'bg-red-100 text-red-700' },
  pending_confirmation: { label: '待確認收款', color: 'bg-red-100 text-red-700' },
  payment_confirmed: { label: '已收款·處理中', color: 'bg-blue-100 text-blue-700' },
  processing: { label: '備貨中', color: 'bg-indigo-100 text-indigo-700' },
  shipped: { label: '已發貨', color: 'bg-cyan-100 text-cyan-700' },
  completed: { label: '訂單完成', color: 'bg-green-100 text-green-700' },
  cancelled: { label: '已取消', color: 'bg-gray-200 text-gray-600' },
};

const getOrderStatus = (order: Order) => {
  const st = (order.status || '').toLowerCase();
  if (st && ORDER_STATUS_META[st]) return ORDER_STATUS_META[st];
  if (order.orderCompleted === 1) return ORDER_STATUS_META.completed;
  if (order.paymentConfirmed === 1) return ORDER_STATUS_META.payment_confirmed;
  return ORDER_STATUS_META.pending_payment;
};

// 待處理（Captain 定義）＝未收款 OR 未標記完成（payment_confirmed / order_completed boolean）；
// 唯一例外：已取消——取消咗嘅單唔會再變完成，唔排除會永世留喺隊列
const isPendingOrder = (order: Order) => {
  if ((order.status || '').toLowerCase() === 'cancelled') return false;
  return order.paymentConfirmed === 0 || order.orderCompleted === 0;
};

const STATUS_FILTER_OPTIONS = [
  { value: 'pending_whatsapp', label: '待客戶確認' },
  { value: 'pending_payment', label: '待付款' },
  { value: 'pending_confirmation', label: '待確認收款' },
  { value: 'payment_confirmed', label: '已收款·處理中' },
  { value: 'processing', label: '備貨中' },
  { value: 'shipped', label: '已發貨' },
  { value: 'completed', label: '已完成' },
  { value: 'cancelled', label: '已取消' },
];

const getOrderTags = (order: Order, allOrders: Order[]): OrderTag[] => {
  const tags: OrderTag[] = [];
  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  // Customer type based on phone
  const customerOrders = allOrders.filter(o => o.phone && o.phone === order.phone);
  const orderCount = customerOrders.length;
  if (orderCount >= 3) {
    tags.push({ key: 'vip', label: 'VIP', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' });
  } else if (orderCount >= 2) {
    tags.push({ key: 'returning-customer', label: '回頭客', color: 'bg-purple-100 text-purple-700 border-purple-200' });
  } else {
    tags.push({ key: 'new-customer', label: '新客戶', color: 'bg-blue-100 text-blue-700 border-blue-200' });
  }

  // Order amount (stored in cents)
  if (order.finalPrice >= 50000) {
    tags.push({ key: 'big-order', label: '大單', color: 'bg-amber-100 text-amber-700 border-amber-200' });
  } else if (order.finalPrice >= 30000) {
    tags.push({ key: 'standard-order', label: '標準單', color: 'bg-green-100 text-green-700 border-green-200' });
  }

  // Referral
  if (order.referralCode) {
    tags.push({ key: 'referred', label: '推薦單', color: 'bg-teal-100 text-teal-700 border-teal-200' });
  }

  return tags;
};

const parseItems = (itemsRaw: any): any[] => {
  try {
    const items = typeof itemsRaw === 'string' ? JSON.parse(itemsRaw) : itemsRaw;
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const itemLabel = (item: any): string => {
  if (item.name) return String(item.name);
  if (item.packageType) return item.packageType === '2-dish-1-soup' ? '2餸1湯' : '3餸1湯';
  return String(item.productId || item.dishName || '商品');
};

// 每個產品一行：名稱打橫排，數量靠右。列表（手機卡片同桌面表格）共用；
// compact（桌面表格）＝名稱一行截斷＋懸停睇全名，避免長名／宣傳句撐高成行。
const OrderItemsRows: React.FC<{ itemsRaw: any; compact?: boolean }> = ({ itemsRaw, compact }) => {
  const items = parseItems(itemsRaw);
  if (items.length === 0) return <span>-</span>;
  return (
    <div className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {items.map((item: any, i: number) => {
        const label = brandifyText(itemLabel(item));
        return (
          <div key={i} className="flex items-baseline justify-between gap-2">
            <span className={`min-w-0 ${compact ? 'truncate' : 'break-words'}`} title={compact ? label : undefined}>{label}</span>
            <span className="whitespace-nowrap shrink-0 text-gray-500 dark:text-gray-400">×{item.quantity || 1}</span>
          </div>
        );
      })}
    </div>
  );
};

interface ProductItem {
  id: number;
  name: string;
  category: string;
}

interface ManualTag {
  id: number;
  tag: string;
  color: string;
}

const TAG_COLORS: Record<string, string> = {
  gray: 'bg-gray-100 text-gray-700 border-gray-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  orange: 'bg-red-100 text-red-700 border-red-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  pink: 'bg-pink-100 text-pink-700 border-pink-200',
};

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [manualTagsMap, setManualTagsMap] = useState<Record<string, ManualTag[]>>({});
  const [filters, setFilters] = useState<Filters>({
    searchTerm: '',
    paymentStatus: '',
    tag: '',
    region: '',
    hasGift: '',
    status: ''
  });
  const [activeTab, setActiveTab] = useState<'pending' | 'all'>('pending');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchOrders();
    fetchProducts();
    fetchManualTags();
    // Check URL query for phone filter from customer list — 落地即開「全部訂單」TAB 預填電話
    const params = new URLSearchParams(window.location.search);
    const phone = params.get('phone');
    if (phone) {
      setActiveTab('all');
      setFilters(prev => ({ ...prev, searchTerm: phone }));
    }
  }, []);

  const fetchProducts = async () => {
    const res = await apiFetch('/api/public/admin/products');
    if (res.ok) {
      const data = await res.json();
      setProducts(data.filter((p: any) => p.category === 'dish' || p.category === 'soup' || p.category === 'item'));
    }
  };

  const fetchManualTags = async () => {
    try {
      const res = await apiFetch('/api/public/admin/customers');
      if (res.ok) {
        const customers = await res.json();
        const map: Record<string, ManualTag[]> = {};
        for (const c of customers) {
          map[c.phone] = c.manualTags || [];
        }
        setManualTagsMap(map);
      }
    } catch (e) {
      console.error('Failed to fetch manual tags:', e);
    }
  };

  const fetchOrders = async () => {
    const res = await apiFetch('/api/public/admin/orders');
    if (res.ok) {
      setOrders(await res.json());
    } else {
      if (res.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定要刪除此訂單嗎？')) return;
    const res = await apiFetch(`/api/public/admin/orders/${id}`, { 
      method: 'DELETE'
    });
    if (res.ok) fetchOrders();
  };

  const handleComplete = async (id: number) => {
    const res = await apiFetch(`/api/public/admin/orders/${id}/complete`, {
      method: 'POST'
    });
    if (res.ok) {
      fetchOrders();
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      navigate('/admin');
    }
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const getReferrer = (order: Order) => {
    if (!order.referralCode) return '-';
    const referrer = orders.find(o => o.phone && o.phone.slice(-4) === order.referralCode && o.id !== order.id);
    return referrer ? referrer.name : '-';
  };

  const clearFilters = () => {
    setFilters({ searchTerm: '', paymentStatus: '', tag: '', region: '', hasGift: '', status: '' });
  };

  const exportToCSV = () => {
    const dishProducts = products.filter(p => p.category === 'dish').map(p => p.name);
    const soupProducts = products.filter(p => p.category === 'soup').map(p => p.name);
    const itemProducts = products.filter(p => p.category === 'item').map(p => p.name);

    const baseHeaders = ['訂單號', '下單日期', '姓名', '電話', '總額(HK$)', '付款狀態', '訂單狀態', '標籤'];
    const productHeaders = [...dishProducts, ...soupProducts, ...itemProducts];
    const headers = [...baseHeaders, ...productHeaders];

    const rows = filteredOrders.map(order => {
      const status = getOrderStatus(order);
      const tags = getOrderTags(order, orders);
      const tagLabels = tags.map(t => t.label).join(';');

      const productCounts: Record<string, number> = {};
      for (const p of productHeaders) productCounts[p] = 0;

      try {
        const items = Array.isArray(order.items) ? order.items : JSON.parse(order.items || '[]');
        for (const item of items) {
          const qty = item.quantity || 1;
          // New format: item.name
          if (item.name && productCounts[item.name] !== undefined) {
            productCounts[item.name] += qty;
          }
          // Old format: selectedDishes + selectedSoup
          for (const dishName of (item.selectedDishes || [])) {
            if (productCounts[dishName] !== undefined) {
              productCounts[dishName] += qty;
            }
          }
          if (item.selectedSoup && productCounts[item.selectedSoup] !== undefined) {
            productCounts[item.selectedSoup] += qty;
          }
        }
      } catch { /* ignore */ }

      const baseRow = [
        order.orderNum || "-",
        new Date(order.createdAt * 1000).toLocaleDateString('zh-HK'),
        order.name,
        order.phone,
        (order.finalPrice / 100).toFixed(0),
        order.paymentConfirmed === 1 ? '已付款' : order.paymentProof ? '待審核' : '未付款',
        status.label,
        tagLabels,
      ];

      const productRow = productHeaders.map(name => productCounts[name] || 0);
      return [...baseRow, ...productRow];
    });

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filteredOrders = orders.filter(order => {
    const orderNum = (order.orderNum || "-");
    const searchLower = filters.searchTerm.toLowerCase();
    const paymentStatusMatch = !filters.paymentStatus || (
      filters.paymentStatus === 'paid' ? order.paymentConfirmed === 1 :
      filters.paymentStatus === 'unpaid' ? order.paymentConfirmed === 0 :
      filters.paymentStatus === 'pending_proof' ? (order.paymentConfirmed === 0 && order.paymentProof) :
      true
    );
    const tagMatch = !filters.tag || (
      getOrderTags(order, orders).some(t => t.key === filters.tag)
    );
    const regionMatch = !filters.region || order.region === filters.region;
    const giftMatch = !filters.hasGift || (
      filters.hasGift === 'yes' ? (order.attachedGiftsCount || 0) > 0 :
      filters.hasGift === 'no' ? (order.attachedGiftsCount || 0) === 0 :
      true
    );
    const statusMatch = !filters.status || (order.status || '').toLowerCase() === filters.status;

    return (
      (orderNum.includes(filters.searchTerm) ||
      String(order.id).includes(filters.searchTerm) ||
      (order.name && order.name.toLowerCase().includes(searchLower)) ||
      (order.phone && order.phone.includes(filters.searchTerm)) ||
      (order.items && JSON.stringify(order.items).toLowerCase().includes(searchLower))) &&
      paymentStatusMatch &&
      tagMatch &&
      regionMatch &&
      giftMatch &&
      statusMatch
    );
  });

  // TAB 1 待處理隊列：唔受篩選器影響，最舊優先（舊單唔會沉底）
  const pendingOrders = orders
    .filter(isPendingOrder)
    .sort((a, b) => a.createdAt - b.createdAt);
  const displayOrders = activeTab === 'pending' ? pendingOrders : filteredOrders;
  const pendingCount = pendingOrders.length;

  const hasActiveFilters = filters.searchTerm || filters.paymentStatus || filters.tag || filters.region || filters.hasGift || filters.status;

  return (
    <AdminLayout currentPage="orders" onLogout={handleLogout}>
      {/* Tabs：待處理隊列／全部訂單＋搜索 */}
      <div className="mb-6 flex gap-1 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <button
          onClick={() => setActiveTab('pending')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'pending'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <ClipboardList className="w-4 h-4" />
          待處理{pendingCount > 0 ? ` (${pendingCount})` : ''}
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
            activeTab === 'all'
              ? 'border-red-600 text-red-600'
              : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
          }`}
        >
          <Search className="w-4 h-4" />
          全部訂單
        </button>
      </div>

      {/* Filters（只喺「全部訂單」TAB 顯示） */}
      {activeTab === 'all' && (
      <div className="mb-6 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">搜尋</label>
            <input
              type="text"
              placeholder="訂單號、姓名、電話..."
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.searchTerm}
              onChange={e => handleFilterChange('searchTerm', e.target.value)}
            />
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">標籤</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.tag}
              onChange={e => handleFilterChange('tag', e.target.value)}
            >
              <option value="">全部標籤</option>
              {ALL_TAGS.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">付款狀態</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.paymentStatus}
              onChange={e => handleFilterChange('paymentStatus', e.target.value)}
            >
              <option value="">全部狀態</option>
              <option value="paid">已付款</option>
              <option value="unpaid">未付款</option>
              <option value="pending_proof">待審核</option>
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">地區</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.region}
              onChange={e => handleFilterChange('region', e.target.value)}
            >
              <option value="">全部地區</option>
              {DELIVERY_REGIONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">隨單禮品</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.hasGift}
              onChange={e => handleFilterChange('hasGift', e.target.value)}
            >
              <option value="">全部</option>
              <option value="yes">有禮品</option>
              <option value="no">無禮品</option>
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">訂單狀態</label>
            <select
              className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white dark:bg-gray-800 dark:text-white"
              value={filters.status}
              onChange={e => handleFilterChange('status', e.target.value)}
            >
              <option value="">全部狀態</option>
              {STATUS_FILTER_OPTIONS.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1 lg:col-span-1 flex items-center justify-end gap-4">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              共 <span className="font-bold text-gray-800 dark:text-white">{filteredOrders.length}</span> 筆
              {hasActiveFilters && <span className="text-red-600 text-xs ml-1">（已篩選）</span>}
            </div>
            <button
              onClick={exportToCSV}
              disabled={filteredOrders.length === 0}
              className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800 font-medium px-2 py-1 border border-green-200 rounded hover:bg-green-50 disabled:opacity-40"
              title="導出當前篩選結果為 CSV"
            >
              <Download size={14} /> CSV
            </button>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-xs text-red-600 hover:text-red-800 font-medium px-2 py-1 border border-red-200 rounded hover:bg-red-50"
              >
                清除
              </button>
            )}
          </div>
        </div>
      </div>
      )}

      {/* 待處理 TAB 提示條（乾淨隊列，篩選請去「全部訂單」） */}
      {activeTab === 'pending' && pendingCount === 0 && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-sm text-green-700 dark:text-green-300">
          🎉 冇待處理訂單，全部清晒！要睇歷史訂單請去「全部訂單」TAB。
        </div>
      )}

      {/* Mobile: per-order cards（產品逐行打橫顯示，唔再迫埋一欄） */}
      <div className="md:hidden space-y-3">
        {displayOrders.map(order => {
          const status = getOrderStatus(order);
          const tags = getOrderTags(order, orders);
          return (
            <div key={order.id} className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <button onClick={() => navigate(`/admin/orders/${order.id}`)} className="font-mono font-bold text-red-600 text-base flex items-center gap-1.5">
                  {(order.attachedGiftsCount || 0) > 0 && <Gift className="w-4 h-4 text-amber-500" />}
                  #{order.orderNum || '-'}
                </button>
                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${status.color}`}>
                  {status.label}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(order.createdAt * 1000).toLocaleDateString('zh-HK')} · {order.name} · {order.phone}
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 text-sm text-gray-700 dark:text-gray-300">
                <OrderItemsRows itemsRaw={order.items} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">總額</span>
                <span className="font-bold text-gray-800 dark:text-white">{formatPrice(order.finalPrice)}</span>
              </div>
              {(order.referralCode || tags.length > 0 || (manualTagsMap[order.phone] || []).length > 0) && (
                <div className="flex flex-wrap items-center gap-1 text-xs">
                  {order.referralCode && (
                    order.referrerPhone ? (
                      <button
                        onClick={() => navigate(`/admin/referrals/${order.referrerPhone}`)}
                        className="font-mono font-bold text-red-600 hover:underline"
                      >
                        {order.referralCode}
                      </button>
                    ) : (
                      <span className="font-mono text-gray-600 dark:text-gray-400">{order.referralCode}</span>
                    )
                  )}
                  {tags.map(tag => (
                    <span
                      key={tag.key}
                      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${tag.color} cursor-pointer hover:opacity-80`}
                      onClick={() => handleFilterChange('tag', tag.key)}
                      title="點擊篩選此標籤"
                    >
                      <Tag size={10} className="mr-0.5" />
                      {tag.label}
                    </span>
                  ))}
                  {(manualTagsMap[order.phone] || [])
                    .filter(mtag => !tags.some(t => t.label === mtag.tag))
                    .map(mtag => (
                      <span
                        key={`m-${mtag.id}`}
                        className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${TAG_COLORS[mtag.color] || TAG_COLORS.gray}`}
                        title="手動標籤"
                      >
                        {mtag.tag}
                      </span>
                    ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={() => navigate(`/admin/orders/${order.id}`)} className="bg-blue-500 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600 transition-colors">詳情</button>
                {order.paymentConfirmed === 1 && order.orderCompleted === 0 && (
                  <button onClick={() => handleComplete(order.id)} className="bg-green-500 text-white px-3 py-1.5 rounded text-xs hover:bg-green-600 transition-colors">標記完成</button>
                )}
                <button onClick={() => handleDelete(order.id)} className="bg-red-500 text-white px-3 py-1.5 rounded text-xs hover:bg-red-600 transition-colors">刪除</button>
              </div>
            </div>
          );
        })}
        {displayOrders.length === 0 && activeTab === 'all' && (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
            {orders.length === 0 ? '暫無訂單' : '沒有找到相符的訂單'}
          </div>
        )}
      </div>

      {/* Orders Table（desktop）：table-fixed＋colgroup 鎖欄寬——短資料欄唔再被產品長名撳到斬字換行 */}
      <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <table className="w-full table-fixed min-w-[1280px]">
          <colgroup>
            <col className="w-[48px]" />
            <col className="w-[112px]" />
            <col className="w-[96px]" />
            <col className="w-[88px]" />
            <col className="w-[118px]" />
            <col className="w-[68px]" />
            <col />
            <col className="w-[84px]" />
            <col className="w-[104px]" />
            <col className="w-[150px]" />
            <col className="w-[190px]" />
          </colgroup>
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <th className="p-3 whitespace-nowrap">ID</th>
              <th className="p-3 whitespace-nowrap">訂單號</th>
              <th className="p-3 whitespace-nowrap">下單日期</th>
              <th className="p-3 whitespace-nowrap">姓名</th>
              <th className="p-3 whitespace-nowrap">電話</th>
              <th className="p-3 whitespace-nowrap">推薦碼</th>
              <th className="p-3">產品</th>
              <th className="p-3 whitespace-nowrap">總額</th>
              <th className="p-3 whitespace-nowrap">訂單狀態</th>
              <th className="p-3 whitespace-nowrap">標籤</th>
              <th className="p-3 whitespace-nowrap">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displayOrders.map(order => {
              const status = getOrderStatus(order);
              const tags = getOrderTags(order, orders);
              return (
                <tr key={order.id} className="hover:bg-red-50 dark:hover:bg-gray-700 transition-colors">
                  <td className="p-3 align-top text-sm text-gray-600 dark:text-gray-400">{order.id}</td>
                  <td className="p-3 align-top font-mono font-bold text-red-600 text-sm whitespace-nowrap">
                    <div className="flex items-center gap-1.5" title="此訂單有隨單禮品">
                      {(order.attachedGiftsCount || 0) > 0 && (
                        <Gift className="w-4 h-4 text-amber-500 shrink-0" />
                      )}
                      {(order.orderNum || "-")}
                    </div>
                  </td>
                  <td className="p-3 align-top text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{new Date(order.createdAt * 1000).toLocaleDateString('zh-HK')}</td>
                  <td className="p-3 align-top text-sm font-medium text-gray-800 dark:text-gray-300 whitespace-nowrap">{order.name}</td>
                  <td className="p-3 align-top text-sm text-gray-600 dark:text-gray-400 font-mono whitespace-nowrap">{order.phone}</td>
                  <td className="p-3 align-top text-sm whitespace-nowrap">
                    {order.referralCode ? (
                      order.referrerPhone ? (
                        <button
                          onClick={() => navigate(`/admin/referrals/${order.referrerPhone}`)}
                          className="font-mono font-bold text-red-600 hover:text-red-800 hover:underline"
                        >
                          {order.referralCode}
                        </button>
                      ) : (
                        <span className="font-mono text-gray-600 dark:text-gray-400">{order.referralCode}</span>
                      )
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="p-3 align-top text-sm text-gray-600 dark:text-gray-400">
                    <OrderItemsRows itemsRaw={order.items} compact />
                  </td>
                  <td className="p-3 align-top text-sm font-bold text-gray-800 dark:text-white whitespace-nowrap">{formatPrice(order.finalPrice)}</td>
                  <td className="p-3 align-top">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${status.color}`}>
                      {status.label}
                    </span>
                  </td>
                  <td className="p-3 align-top">
                    <div className="flex flex-wrap gap-1">
                      {tags.map(tag => (
                        <span
                          key={tag.key}
                          className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${tag.color} cursor-pointer hover:opacity-80`}
                          onClick={() => { setActiveTab('all'); handleFilterChange('tag', tag.key); }}
                          title="點擊篩選此標籤"
                        >
                          <Tag size={10} className="mr-0.5" />
                          {tag.label}
                        </span>
                      ))}
                      {(manualTagsMap[order.phone] || [])
                        .filter(mtag => !tags.some(t => t.label === mtag.tag))
                        .map(mtag => (
                          <span
                            key={`m-${mtag.id}`}
                            className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${TAG_COLORS[mtag.color] || TAG_COLORS.gray}`}
                            title="手動標籤"
                          >
                            {mtag.tag}
                          </span>
                        ))}
                    </div>
                  </td>
                  <td className="p-3 align-top whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button onClick={() => navigate(`/admin/orders/${order.id}`)} className="bg-blue-500 text-white px-3 py-1.5 rounded text-xs hover:bg-blue-600 transition-colors">詳情</button>
                      {order.paymentConfirmed === 1 && order.orderCompleted === 0 && (
                        <button onClick={() => handleComplete(order.id)} className="bg-green-500 text-white px-3 py-1.5 rounded text-xs hover:bg-green-600 transition-colors">標記完成</button>
                      )}
                      <button onClick={() => handleDelete(order.id)} title="刪除訂單" className="bg-red-500 text-white p-1.5 rounded hover:bg-red-600 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {displayOrders.length === 0 && activeTab === 'all' && (
              <tr>
                <td colSpan={11} className="p-8 text-center text-gray-500 dark:text-gray-400">
                  {orders.length === 0 ? '暫無訂單' : '沒有找到相符的訂單'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
};

export default AdminOrders;
